if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.currentVariant = null;
        this.variantImages = new Map(); // Store variant images mapping
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.initializeVariantImages();
        
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(event);
          
          // Get current variant based on selected options
          this.updateCurrentVariant();
          
          // Handle gallery visibility
          this.handleGalleryVisibility();
          
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        // Initialize on load
        this.updateCurrentVariant();
        this.handleGalleryVisibility();
      }

      initializeVariantImages() {
        // Get product data from window or data attributes
        const productData = window.productData || this.getProductData();
        if (productData && productData.variants) {
          productData.variants.forEach(variant => {
            if (variant.featured_image && variant.featured_image.id) {
              this.variantImages.set(variant.id, variant.featured_image.id);
            }
          });
        }
      }

      getProductData() {
        // Try to get product data from various sources
        const productJson = document.querySelector('[data-product-json]');
        if (productJson) {
          try {
            return JSON.parse(productJson.textContent);
          } catch (e) {
            console.warn('Could not parse product data');
          }
        }
        return null;
      }

      updateCurrentVariant() {
        const productData = window.productData || this.getProductData();
        if (!productData) return;

        const selectedValues = this.selectedOptionValues;
        
        // Find matching variant
        this.currentVariant = productData.variants.find(variant => {
          return variant.option_values.every((optionValue, index) => {
            return selectedValues[index] === optionValue.id.toString();
          });
        });
      }

      handleGalleryVisibility() {
        const mediaGallery = document.querySelector('media-gallery');
        if (!mediaGallery) return;

        const gallerySlides = mediaGallery.querySelectorAll('.product__media-list .swiper-slide');
        
        // Get current variant's featured image ID
        const currentVariantImageId = this.currentVariant && this.currentVariant.featured_image 
          ? this.currentVariant.featured_image.id 
          : null;

        // Handle main gallery slides
        gallerySlides.forEach(slide => {
          const mediaId = slide.getAttribute('data-media-id');
          const isVariantImage = slide.hasAttribute('data-variant-image') || slide.classList.contains('variant-image-slide');
          
          if (isVariantImage) {
            // Show only if it's the current variant's image
            if (currentVariantImageId && mediaId == currentVariantImageId) {
              slide.classList.add('show-variant-image');
              slide.classList.remove('swiper-slide-hidden');
            } else {
              slide.classList.remove('show-variant-image');
              slide.classList.add('swiper-slide-hidden');
            }
          } else {
            // Always show non-variant images
            slide.classList.remove('swiper-slide-hidden');
          }
        });

        // Thumbnails are already hidden via CSS and HTML structure
        // No need to handle them in JavaScript since they're not rendered for variant images

        // Update swiper if it exists
        this.updateSwiper();
      }

      updateSwiper() {
        // Update Swiper instances if they exist
        const mediaGallery = document.querySelector('media-gallery');
        if (!mediaGallery) return;

        // Update main gallery swiper
        const mainSwiper = mediaGallery.swiper || mediaGallery.querySelector('.swiper')?.swiper;
        if (mainSwiper) {
          mainSwiper.update();
          mainSwiper.updateSlides();
        }

        // Update thumbnail swiper
        const thumbnailsSwiper = mediaGallery.querySelector('.product__media-gallery-thumbails')?.swiper;
        if (thumbnailsSwiper) {
          thumbnailsSwiper.update();
          thumbnailsSwiper.updateSlides();
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;
        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');
          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }
          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}